import { sanitizeAuthNextPath } from '@helix/lib';

const POST_LOGOUT_STATE_KEY = 'helix_post_logout_state';
const POST_LOGOUT_STATE_TTL_MS = 15 * 60 * 1000;

type StoredPostLogoutState = {
  nextPath: string;
  signedOut: true;
  createdAtMs: number;
};

export type PostLogoutState = {
  nextPath: string;
  signedOut: true;
};

export function storePostLogoutState(next?: string | null): void {
  if (typeof window === 'undefined') {
    return;
  }

  const payload: StoredPostLogoutState = {
    nextPath: sanitizeAuthNextPath(next),
    signedOut: true,
    createdAtMs: Date.now(),
  };

  try {
    window.sessionStorage.setItem(POST_LOGOUT_STATE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore best-effort browser storage failures.
  }
}

export function readPostLogoutState(): PostLogoutState | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(POST_LOGOUT_STATE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredPostLogoutState>;
    const createdAtMs = typeof parsed.createdAtMs === 'number' ? parsed.createdAtMs : NaN;
    const isExpired = !Number.isFinite(createdAtMs) || Date.now() - createdAtMs > POST_LOGOUT_STATE_TTL_MS;
    if (parsed.signedOut !== true || isExpired) {
      clearPostLogoutState();
      return null;
    }

    return {
      nextPath: sanitizeAuthNextPath(parsed.nextPath),
      signedOut: true,
    };
  } catch {
    clearPostLogoutState();
    return null;
  }
}

export function clearPostLogoutState(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem(POST_LOGOUT_STATE_KEY);
  } catch {
    // Ignore best-effort browser storage failures.
  }
}
